import { GraduationCap, Award, CheckCircle, Zap, Users } from "lucide-react";
import { useTranslation } from 'react-i18next';
import mathiasPhoto from '../assets/img/mathias.webp';

const About = () => {
    const { t } = useTranslation();

    const credentials = [
        { icon: GraduationCap, label: t('about.credentials.bachelor') },
        { icon: Award, label: t('about.credentials.awsDeveloper') },
        { icon: Award, label: t('about.credentials.awsPractitioner') },
    ];

    const values = [
        {
            icon: Zap,
            title: t('about.values.fastDelivery.title'),
            description: t('about.values.fastDelivery.description'),
        },
        {
            icon: Users,
            title: t('about.values.partnership.title'),
            description: t('about.values.partnership.description'),
        },
        {
            icon: CheckCircle,
            title: t('about.values.ownership.title'),
            description: t('about.values.ownership.description'),
        },
    ];

    return (
        <section id="about" className="py-24 bg-slate-900" aria-labelledby="about-heading">
            <div className="container mx-auto px-6">
                {/* Header */}
                <div className="text-center mb-16">
                    <span className="text-blue-400 text-sm font-semibold uppercase tracking-widest mb-3 block">
                        {t('about.eyebrow')}
                    </span>
                    <h2 id="about-heading" className="text-4xl md:text-5xl font-bold text-white mb-4">
                        {t('about.title')}
                    </h2>
                    <p className="text-lg text-gray-400 max-w-xl mx-auto">
                        {t('about.bio1')}
                    </p>
                </div>

                {/* Mathias section */}
                <div className="grid lg:grid-cols-2 gap-10 items-start mb-16 max-w-5xl mx-auto">
                    {/* Left: photo + credentials — right-aligned, locked to photo width */}
                    <div className="flex justify-center lg:justify-end">
                        <div className="flex flex-col items-center gap-5 w-72">
                            <div className="relative">
                                <div className="w-56 h-56 rounded-2xl overflow-hidden border-2 border-blue-500/30 shadow-2xl shadow-blue-500/10">
                                    <img
                                        src={mathiasPhoto}
                                        alt="Mathias Ranna — FinfraTech"
                                        className="w-full h-full object-cover object-center"
                                    />
                                </div>
                                {/* Available badge */}
                                <div className="absolute -bottom-3 -right-3 flex items-center gap-2 bg-slate-800 border border-slate-700 rounded-full px-3 py-1.5 shadow-lg">
                                    <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" aria-hidden="true" />
                                    <span className="text-xs text-gray-300 font-medium">{t('about.available')}</span>
                                </div>
                            </div>

                            {/* Credentials — match photo width */}
                            <div className="flex flex-col gap-3">
                                {credentials.map((cred) => (
                                    <div
                                        key={cred.label}
                                        className="flex items-center gap-3 bg-slate-800/60 border border-slate-700/60 rounded-xl px-4 py-3"
                                    >
                                        <cred.icon className="w-5 h-5 text-blue-400 flex-shrink-0" aria-hidden="true" />
                                        <span className="text-sm text-gray-300 font-medium">{cred.label}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Right: bio text */}
                    <div>
                        <h3 className="text-2xl font-bold text-white mb-2">{t('about.bioName')}</h3>
                        <p className="text-blue-400 text-sm font-semibold uppercase tracking-wide mb-6">{t('about.bioRole')}</p>

                        <p className="text-gray-300 leading-relaxed mb-4">
                            {t('about.bio2')}
                        </p>
                        <p className="text-gray-400 leading-relaxed">
                            {t('about.bio3')}
                        </p>
                    </div>
                </div>

                {/* Values grid */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-5xl mx-auto">
                    {values.map((val) => (
                        <div
                            key={val.title}
                            className="bg-slate-800/50 border border-slate-700/60 rounded-xl p-5 hover:border-blue-500/30 transition-colors duration-200"
                        >
                            <val.icon className="w-5 h-5 text-blue-400 mb-3" aria-hidden="true" />
                            <h4 className="text-white font-semibold text-sm mb-1.5">{val.title}</h4>
                            <p className="text-gray-400 text-xs leading-relaxed">{val.description}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default About;
